import {HTMLAttributes} from 'react'
import {formatLocaleDate} from '@/dates.ts'
import {differenceInCalendarDays} from 'date-fns'
import {capitalize, cn} from '@utilities/helpers.ts'
import {Assignment} from '@features/assignment/types.ts'
import {TableBodyCellText} from '@components/ui/table-atoms'
import {useTranslation} from '@/translations/i18n.tsx'
import {formatPaymentAmount} from '@features/payments/utils.ts'

export const PrimaryMobileTableRow = ({className, ...props}: HTMLAttributes<HTMLDivElement>) => (
    <div className={cn('grid w-full grid-cols-[minmax(0,1fr)_auto] gap-2 pb-3', className)} {...props} />
)

export const PaymentsExpandedRows = ({assignment}: {assignment: Assignment}) => {
    return (
        <div>
            {!!assignment.payments &&
                assignment.payments.map((transaction, index) => (
                    <div
                        key={index}
                        className="flex w-full justify-between py-4 pr-0 pl-2 sm:pl-4 first:border-t first:border-neutral-300 not-last:border-b not-last:border-neutral-300 [&_p]:text-success-600"
                    >
                        <div className="flex flex-col gap-0.5">
                            <h3>{capitalize(transaction.payment_type)}</h3>
                            <span>
                                {transaction.payment_date ? formatLocaleDate(transaction.payment_date, 'PPp') : '-'}
                            </span>
                        </div>
                        <p>
                            {!!transaction.payout_amount &&
                                formatPaymentAmount({
                                    amount: transaction.payout_photographer_currency_amount,
                                    amountUsd: transaction.payout_amount ?? 0,
                                    currency: transaction.payout_photographer_currency,
                                    prefix: '+'
                                })}
                        </p>
                    </div>
                ))}
        </div>
    )
}

export const TableAreaItem = ({assignment}: {assignment: Assignment}) => {
    return (
        <TableBodyCellText
            variant={'dark'}
            title={assignment.entity.address || `${assignment.entity.city}(${assignment.entity.country})` || undefined}
        >
            {assignment.entity.address || `${assignment.entity.city}(${assignment.entity.country})` || '-'}
        </TableBodyCellText>
    )
}

export const TableDateItems = ({date}: {date: string}) => {
    const {t} = useTranslation()

    const differenceInDays = differenceInCalendarDays(new Date(date), new Date())

    return (
        <div className="flex w-full flex-row justify-between gap-0.5 md:flex-col md:justify-start [&_p]:text-sm [&_p]:text-neutral-900 [&_span]:text-xs [&_span]:text-neutral-700">
            <p>{formatLocaleDate(date, 'PPp')}</p>
            <span>
                {differenceInDays < 0
                    ? t('home:table:x_days_ago', {count: Math.abs(differenceInDays)})
                    : t('home:table:in_x_days', {count: differenceInDays})}
            </span>
        </div>
    )
}

export const RequiredActionsTitle = ({
    variant = 'neutral',
    className,
    ...props
}: HTMLAttributes<HTMLHeadingElement> & {variant?: 'neutral' | 'blue'}) => (
    <h4
        className={cn('text-md font-medium', variant === 'neutral' ? 'text-neutral-900' : 'text-blue-600', className)}
        {...props}
    />
)

export const SlidingPendingActionModalHeader = ({className, ...props}: HTMLAttributes<HTMLDivElement>) => (
    <div className={cn('py-2.5 px-2', className)} {...props} />
)
